import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        datePublished: article.date,
        url: `https://www.baicaiwang.com/article/${article.slug}`,
        author: { '@type': 'Organization', name: '白菜网站' },
        publisher: { '@type': 'Organization', name: '白菜网站' },
        keywords: article.tags.join(','),
        articleSection: article.categoryName,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.baicaiwang.com/' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://www.baicaiwang.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'WebPage',
        name: article.title,
        url: `https://www.baicaiwang.com/article/${article.slug}`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.baicaiwang.com/' },
            { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://www.baicaiwang.com/category/${article.category}` },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        },
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${article.title} - 白菜网站`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.baicaiwang.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
      />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main>
        <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-lg bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                {article.hot && (
                  <span className="px-3 py-1 rounded-lg bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                    热门
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万次阅读` : `${article.views}次阅读`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-store-line"></i>
                  来源：{article.source}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative w-full h-64 md:h-96 rounded-xl overflow-hidden bg-background-200 mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 mt-8 pt-6 border-t border-background-200/70 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-lg bg-background-100 text-foreground-600 text-xs whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500">分享到：</span>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" rel="nofollow" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" rel="nofollow" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" rel="nofollow" aria-label="复制链接">
                <i className="ri-link"></i>
              </a>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}